/**
 * Client-side image compression using the browser Canvas API.
 * Resizes the image so the longest edge is at most `maxDimension` pixels
 * and encodes as JPEG at the given quality. If the original file is already
 * small enough (≤ `maxBytes`) it is returned unchanged.
 */
export default async function compressImage(file, {
  maxDimension = 2400,
  quality = 0.82,
  maxBytes = 9 * 1024 * 1024 // 9 MB (leave headroom below 10 MB server limit)
} = {}) {
  // Skip non-image files
  if (!file.type.startsWith("image/")) return file;

  // If already under limit, return as-is
  if (file.size <= maxBytes) return file;
  const bitmap = await createImageBitmap(file);
  const {
    width,
    height
  } = bitmap;

  // Calculate scaled dimensions (keep aspect ratio)
  let targetW = width;
  let targetH = height;
  if (width > maxDimension || height > maxDimension) {
    const ratio = Math.min(maxDimension / width, maxDimension / height);
    targetW = Math.round(width * ratio);
    targetH = Math.round(height * ratio);
  }
  const canvas = document.createElement("canvas");
  canvas.width = targetW;
  canvas.height = targetH;
  const ctx = canvas.getContext("2d");
  if (!ctx) return file; // fallback: return original if canvas unsupported
  ctx.drawImage(bitmap, 0, 0, targetW, targetH);
  bitmap.close();

  // Try progressively lower quality until under maxBytes
  let currentQuality = quality;
  for (let attempt = 0; attempt < 4; attempt++) {
    const blob = await new Promise(resolve => canvas.toBlob(b => resolve(b), "image/jpeg", currentQuality));
    if (blob.size <= maxBytes || currentQuality <= 0.4) {
      const name = file.name.replace(/\.[^.]+$/, ".jpg");
      return new File([blob], name, {
        type: "image/jpeg"
      });
    }
    currentQuality -= 0.12;
  }

  // Final fallback at minimum quality
  const blob = await new Promise(resolve => canvas.toBlob(b => resolve(b), "image/jpeg", 0.3));
  const name = file.name.replace(/\.[^.]+$/, ".jpg");
  return new File([blob], name, {
    type: "image/jpeg"
  });
}